import { assert } from "@ember/debug";
import Service, { service } from "@ember/service";
import { tracked } from "@glimmer/tracking";
import {
  getItemFromLocalStorage,
  setItemInLocalStorage
} from "parog-web/utils/use-local-storage";

const NAVIGATION_STORAGE_EMBEDDED_KEY = "service:navigation:embedded";

export default class NavigationService extends Service {
  @service router;
  @service session;

  // Private properties
  @tracked _shouldGoBack = false;
  /**
   * How deep we are in the history
   *
   * This counter is incremented everytime the router transitions without the intent to go back in the history.
   * On the other hand, if the transition was initiated with the back action, this counter is decremented
   *
   * When depth === 1, there is no history to go back to
   *
   * @property depth
   * @type {Number}
   */
  @tracked depth = 1;

  get canGoBack(): boolean {
    return this.depth > 1;
  }

  get currentPatientRouteName(): string {
    return this.session.patient
      ? "patient-web.protected"
      : "medics-web.protected.patients.patient";
  }

  get currentRouteName(): string {
    return this.router.currentRouteName;
  }

  /**
   * Is the application is inside an iframe
   */
  get isEmbedded(): boolean {
    const value = getItemFromLocalStorage(NAVIGATION_STORAGE_EMBEDDED_KEY);
    return value ? Boolean(JSON.parse(value)) : false;
  }

  set isEmbedded(val: boolean) {
    setItemInLocalStorage(NAVIGATION_STORAGE_EMBEDDED_KEY, val);
    console.debug(`${val ? "Activate" : "Deactivate"} embedded navigation`);
  }

  get isStandalone(): boolean {
    return !this.isEmbedded;
  }

  constructor(owner) {
    super(owner);
    this.router.on("routeDidChange", this._updateDepth.bind(this));
  }

  _updateDepth({
    from,
    to
  }: {
    from: { name: string };
    to: { name: string };
  }): void | number {
    // If there is no from, we are landing on the app, so depth should not be touched
    if (!from) return;

    // If we are transitioning to the same route, do nothing
    if (from.name === to.name) return;

    // increment depth and early return if we do not want to go back
    if (!this._shouldGoBack) return (this.depth = this.depth + 1);

    // We want to go back, let's decrement depth and reset shouldGoBack
    this.depth = this.depth - 1;
    this._shouldGoBack = false;
  }

  back = (): void => {
    assert(
      `You are trying to use NavigationService.back(), but the current location implementation '${this.router.location.implementation}' does not support the browser's history API`,
      !!this.router.location.history
    );
    this._shouldGoBack = true;
    this.router.location.history.back();
  };

  getAbsoluteRouteFromContextual(
    contextualRoute: string | undefined
  ): string | undefined {
    return contextualRoute
      ? `${this.currentPatientRouteName}.${contextualRoute}`
      : undefined;
  }
}

declare module "@ember/service" {
  interface Registry {
    navigation: NavigationService;
  }
}
